// LogDiveForm.js (updated)
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  FormHelperText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import DiveShareModal from "./DiveShareModal";

// Import all the component parts
import DiveStartDateTime from "./DiveDateTime.js"; // Import the new component
import DiveEnvironmentalInfo from "./DiveEnvironmentalInfo";
import DiveCreatureSelector from "./DiveCreatureSelector";
import DivePhotoUploader from "./DivePhotoUploader";
import DiveCopyrightAgreement from "./DiveCopyrightAgreement";
import DiveSharedOption from "./DiveSharedOption";
import DiveSubmissionControls from "./DiveSubmissionControls";
import DiveSuccessSnackbar from "./DiveSuccessSnackbar";



const LogDiveForm = (props) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { currentUser, userObjectId } = useAuth();
  const {
    divesiteid,
    preFillData,
    onSubmitSuccess,
    isClaimMode = false,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const diver = userObjectId;
  const divesite = props.divesiteid;

  // Form state
  const [diveDate, setDiveDate] = useState(() => {
    if (preFillData?.diveDate) {
      // Convert the ISO string to a date string suitable for the input type="date"
      const date = new Date(preFillData.diveDate);
      return date.toISOString().split("T")[0];
    }
    return "";
  });

  
  
  // New state for dive start time, duration, and timezone
  const [diveStartTime, setDiveStartTime] = useState(() => {
    if (preFillData?.diveStartTime) {
      return preFillData.diveStartTime;
    }
    return "";
  });
  
  const [diveDuration, setDiveDuration] = useState(() => {
    if (preFillData?.diveDuration) {
      return preFillData.diveDuration;
    }
    return "";
  });
  
  const [diveTimeZone, setDiveTimeZone] = useState(() => {
    if (preFillData?.diveTimeZone) {
      return preFillData.diveTimeZone;
    }
    return "";
  });
  
  const [visibilityRating, setVisibilityRating] = useState(
    preFillData?.visibilityRating || 0
  );
  const [waterTemperature, setWaterTemperature] = useState(
    preFillData?.waterTemperature || ""
  );
  const [diveRating, setDiveRating] = useState(preFillData?.diveRating || 0);
  const [comments, setComments] = useState(preFillData?.comments || "");

  // Validation state
  const [visibilityRatingError, setVisibilityRatingError] = useState(null);
  const [diveRatingError, setDiveRatingError] = useState(null);
  const [commentsError, setCommentsError] = useState(null);
  const [error, setError] = useState(null);

  // Creatures state
  const [creatures, setCreatures] = useState([]);
  const [selectedCreatures, setSelectedCreatures] = useState([]);
  const [loadingCreatures, setLoadingCreatures] = useState(true);
  const [inputValue, setInputValue] = useState("");

  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Photo state
  const [diveImages, setDiveImages] = useState([]);
  const [photosUploadedAndAgreed, setPhotosUploadedAndAgreed] = useState(false);

  // Shared dive state
  const [createSharedDive, setCreateSharedDive] = useState(false);
  const [sharedDiveUrl, setSharedDiveUrl] = useState("");

  // UI state
  const [success, setSuccess] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(
    "Dive logged successfully."
  );

  // Load creatures on component mount
  useEffect(() => {
    const fetchCreatures = async () => {
      setLoadingCreatures(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await axios.get(
          "/api/creatures",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCreatures(
          response.data.map((creature) => ({
            label: creature.commonName,
            value: creature._id,
          }))
        );
        setLoadingCreatures(false);
      } catch (error) {
        console.error("Error fetching creatures:", error);
        setError("Failed to load creatures.");
        setLoadingCreatures(false);
      }
    };

    if (currentUser) {
      fetchCreatures();
    } else {
      setLoadingCreatures(false);
    }
  }, [currentUser]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    let isValid = true;

    // Validation checks
    if (visibilityRating === 0) {
      setVisibilityRatingError("Visibility rating is required");
      isValid = false;
    } else {
      setVisibilityRatingError(null);
    }

    if (diveRating === 0) {
      setDiveRatingError("Dive rating is required");
      isValid = false;
    } else {
      setDiveRatingError(null);
    }

    if (comments.trim() === "") {
      setCommentsError("Comments are required");
      isValid = false;
    } else {
      setCommentsError(null);
    }

    // Check if images are uploaded and agreement is checked
    if (diveImages.length > 0 && !photosUploadedAndAgreed) {
      setError("You must certify that you own the rights to the uploaded images");
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    // Process creatures data
    const finalCreatures = selectedCreatures.map((creature) => creature.value);
    if (
      inputValue &&
      !creatures.find((c) => c.label.toLowerCase() === inputValue.toLowerCase())
    ) {
      finalCreatures.push(inputValue);
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null); // Clear any previous errors
      const token = await currentUser.getIdToken();

      // Create FormData
      const formData = new FormData();
      formData.append("diver", diver);
      formData.append("divesite", divesite);
      formData.append("diveDate", diveDate);
      formData.append("diveStartTime", diveStartTime); // Add dive start time
      formData.append("diveTimeZone", diveTimeZone);
      formData.append("diveDuration", diveDuration); // Add dive duration
      formData.append("visibilityRating", visibilityRating);
      formData.append("waterTemperature", waterTemperature);
      formData.append("diveRating", diveRating);
      formData.append("comments", comments);
      formData.append("creatures", JSON.stringify(finalCreatures));

      // Only include the shared dive flag if not in claim mode
      if (!isClaimMode) {
        formData.append("createSharedDive", createSharedDive);
      }

      // If in claim mode and we have seedDive ID from preFillData
      if (isClaimMode && preFillData?._id) {
        formData.append("seedDive", preFillData._id);
        formData.append("isProvisional", true);
      }

      // Process images and descriptions
      const mediaImageDescriptions = [];
      if (diveImages?.length > 0) {
        diveImages.forEach((imageObject) => {
          if (imageObject?.file) {
            formData.append(`mediaImages`, imageObject.file);
            mediaImageDescriptions.push(imageObject.description || "");
          }
        });
        formData.append(
          "mediaImageDescriptions",
          JSON.stringify(mediaImageDescriptions)
        );
      }

      // Determine which API endpoint to use
      const apiEndpoint = isClaimMode
        ? `/api/dives/update/${preFillData._id}`
        : "/api/dives/create";

      // *** ADDED CONSOLE LOGGING HERE ***
      console.log("Form Data:", Object.fromEntries(formData.entries())); // Log the form data
      console.log("API Endpoint:", apiEndpoint);
      console.log("Token:", token);
      // *** END ADDED CONSOLE LOGGING HERE ***

      try {
        // Use axios.put for updates instead of axios.post
        const httpMethod = isClaimMode ? axios.put : axios.post;

        const response = await httpMethod(apiEndpoint, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        });

        const newDiveId = response.data._id;

        // Call the onSubmitSuccess callback if provided (for claim mode)
        if (onSubmitSuccess && typeof onSubmitSuccess === "function") {
          onSubmitSuccess(response.data);
        }

        // Handle shared dive creation if not in claim mode
        if (!isClaimMode && createSharedDive) {
          try {
            const sharedDiveResponse = await axios.post(
              `/api/sharing/share/${newDiveId}`,
              {},
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            const { token: sharedDiveToken } = sharedDiveResponse.data;

            // Construct the URL for the token in the QR code
            const sharedDiveUrl = `https://www.mydivingsites.com/claim/${sharedDiveToken}`;
            setSharedDiveUrl(sharedDiveUrl);
            setShowShareModal(true);
            setSnackbarMessage("Dive logged successfully!");
            setOpenSnackbar(true);
          } catch (error) {
            console.error("Error creating shared dive:", error);
            setError("Failed to create shared dive.");
            setSnackbarMessage("Dive logged successfully."); // Still show success message
            setOpenSnackbar(true);
          }
        } else {
          setSnackbarMessage(
            isClaimMode
              ? "Dive claim completed successfully!"
              : "Dive logged successfully."
          );
          setOpenSnackbar(true);
        }

        setSuccess(true);
        setIsUploading(false);

        // Reset form
        resetForm();
      } catch (error) {
        console.error("Error logging dive:", error);

        // Enhanced error handling
        if (error.response) {
          // The server responded with an error
          if (error.response.status === 409) {
            // Handle 409 Conflict - duplicate key error
            setError(
              "This dive record has already been claimed or created. Please refresh and try again."
            );
          } else if (error.response.status === 404) {
            // Handle 404 Not Found - dive might have been deleted
            setError(
              "This dive record could not be found. It may have been deleted."
            );
          } else {
            // Handle other server error responses
            setError(
              error.response.data.message || "Server error. Please try again."
            );
          }
        } else if (error.request) {
          // The request was made but no response was received
          setError("No response from server. Please check your connection.");
        } else {
          // Something happened in setting up the request
          setError("Error preparing request: " + error.message);
        }

        setIsUploading(false);
        setOpenSnackbar(true);
        setSuccess(false);
        setSnackbarMessage("Failed to log dive.");
      }
    } catch (generalError) {
      // Handle errors outside the axios request
      console.error("General error:", generalError);
      setError("An unexpected error occurred: " + generalError.message);
      setIsUploading(false);
      setOpenSnackbar(true);
      setSuccess(false);
      setSnackbarMessage("Failed to log dive.");
    }
  };

  const resetForm = () => {
    setDiveDate("");
    setDiveStartTime("");
    setDiveDuration("");
    setDiveTimeZone(""); 
    setVisibilityRating(0);
    setWaterTemperature("");
    setDiveRating(0);
    setComments("");
    setSelectedCreatures([]);
    setInputValue("");
    setDiveImages([]);
    setPhotosUploadedAndAgreed(false);
    setCreateSharedDive(false);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  if (loadingCreatures) {
    return <Typography>Loading Creatures...</Typography>;
  }

  return (
    <>
      <Card sx={{ width: isMobile ? "100%" : 500, maxWidth: "100%" }}>
        <CardContent sx={{ width: "100%" }}>
          <Typography variant="h5" component="h2" gutterBottom>
            {isClaimMode ? "Log your details" : "Log a New Dive"}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Replace the old date field with the new DiveStartDateTime component */}
              <DiveStartDateTime
                diveDate={diveDate}
                setDiveDate={setDiveDate}
                diveStartTime={diveStartTime}
                setDiveStartTime={setDiveStartTime}
                diveDuration={diveDuration}
                setDiveDuration={setDiveDuration}
                diveTimeZone={diveTimeZone}
                setDiveTimeZone={setDiveTimeZone}
                disabled={isClaimMode} // Disable if in claim mode as date is inherited
              />

              {/* Environmental Information Section */}
              <DiveEnvironmentalInfo
                visibilityRating={visibilityRating}
                setVisibilityRating={setVisibilityRating}
                visibilityRatingError={visibilityRatingError}
                diveRating={diveRating}
                setDiveRating={setDiveRating}
                diveRatingError={diveRatingError}
                waterTemperature={waterTemperature}
                setWaterTemperature={setWaterTemperature}
              />

              {/* Comments Section */}
              <TextField
                id="comments"
                label="Comments"
                multiline
                rows={4}
                value={comments}
                onChange={(event) => setComments(event.target.value)}
              />
              {commentsError && (
                <FormHelperText error>{commentsError}</FormHelperText>
              )}

              {/* Creatures Seen Section */}
              <DiveCreatureSelector
                creatures={creatures}
                selectedCreatures={selectedCreatures}
                setSelectedCreatures={setSelectedCreatures}
                inputValue={inputValue}
                setInputValue={setInputValue}
              />

              {/* Photo Upload Section */}
              <DivePhotoUploader
                diveImages={diveImages}
                setDiveImages={setDiveImages}
              />

              {/* Copyright Agreement Section */}
              {diveImages.length > 0 && (
                <DiveCopyrightAgreement
                  photosUploadedAndAgreed={photosUploadedAndAgreed}
                  setPhotosUploadedAndAgreed={setPhotosUploadedAndAgreed}
                  currentUser={currentUser}
                />
              )}

              {/* Shared Dive Option Section - only show if not in claim mode */}
              {!isClaimMode && (
                <DiveSharedOption
                  createSharedDive={createSharedDive}
                  setCreateSharedDive={setCreateSharedDive}
                />
              )}

              {error && <FormHelperText error>{error}</FormHelperText>}

              {/* Submission Controls */}
              <DiveSubmissionControls
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                isClaimMode={isClaimMode}
              />
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Success/Error Snackbar */}
      <DiveSuccessSnackbar
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        success={success}
        message={snackbarMessage}
        sharedDiveUrl={""}
        isMobile={isMobile}
      />
      <DiveShareModal
        open={showShareModal}
        onClose={() => setShowShareModal(false)}
        sharedDiveUrl={sharedDiveUrl}
        isMobile={isMobile}
      />
    </>
  );
};

export default LogDiveForm;
export { LogDiveForm };
